import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faSpinner, faUniversity, faMoneyBillWave, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { walletAPI, userAPI } from '../services/api';
import '../assets/css/wallet-modals.css';

const MIN_WITHDRAWAL = 1000;

/**
 * Wallet withdrawal modal shared by every dashboard (buyer, premium buyer,
 * and any seller surface that adopts it).
 *
 * Withdrawal is a wallet capability, not a seller capability — the backend
 * route (POST /wallet/withdraw) lives in the plain authenticated group, so
 * any signed-in user may withdraw. Buyers accumulate real balances from
 * refunds, cancelled orders and escrow releases, so they need this too.
 *
 * The backend requires bank_code alongside the account details in order to
 * initiate the Monnify disbursement. A manually typed bank name has no code,
 * so withdrawal is deliberately restricted to a SAVED bank account — that is
 * the only source the code can come from.
 */
const WalletWithdrawModal = ({ isOpen, onClose, balance = 0, isDarkMode = false, onSuccess, onError }) => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const response = await userAPI.getBankAccounts();
      const data = response?.data || response;
      setBankAccounts(data?.bank_accounts || []);
    } catch {
      setBankAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setSelectedBankId('');
      setError('');
      fetchAccounts();
    }
  }, [isOpen, fetchAccounts]);

  if (!isOpen) return null;

  const selectedAccount = bankAccounts.find(a => (a.id || a._id) === selectedBankId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const value = parseFloat(amount);
    if (isNaN(value) || value < MIN_WITHDRAWAL) {
      setError(`Minimum withdrawal amount is ₦${MIN_WITHDRAWAL.toLocaleString()}`);
      return;
    }
    if (value > balance) {
      setError('Insufficient wallet balance');
      return;
    }
    if (!selectedAccount) {
      setError('Select the bank account to withdraw to');
      return;
    }
    if (!selectedAccount.bank_code) {
      setError('This saved account has no bank code. Re-add it from Bank Accounts before withdrawing.');
      return;
    }

    setSubmitting(true);
    try {
      await walletAPI.requestWithdrawal({
        amount: value,
        bank_account: selectedAccount.account_number,
        bank_name: selectedAccount.bank_name,
        bank_code: selectedAccount.bank_code,
        account_name: selectedAccount.account_name,
      });
      onSuccess?.(value);
      onClose?.();
    } catch (err) {
      const message = err?.message || 'Failed to process withdrawal. Please try again.';
      setError(message);
      onError?.(message);
    } finally {
      setSubmitting(false);
    }
  };

  const labelColor = isDarkMode ? '#D1D5DB' : '#374151';
  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '8px',
    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.15)' : '#E5E7EB'}`,
    background: isDarkMode ? 'rgba(0,0,0,0.25)' : '#fff',
    color: isDarkMode ? '#F9FAFB' : '#111827',
    fontSize: '1rem',
    fontFamily: 'inherit',
  };

  return (
    <div className="AutoboyExpress-dash-modal-overlay" onClick={onClose}>
      <div
        className="AutoboyExpress-dash-modal"
        onClick={e => e.stopPropagation()}
        style={isDarkMode ? { background: '#111827', color: '#F9FAFB' } : undefined}
      >
        <div className="AutoboyExpress-dash-modal-header">
          <h2><FontAwesomeIcon icon={faMoneyBillWave} /> Withdraw Funds</h2>
          <button type="button" className="AutoboyExpress-dash-modal-close" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '1.5rem 2rem' }}>
            <div style={{
              padding: '0.9rem 1rem',
              borderRadius: '10px',
              background: isDarkMode ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.1)',
              border: '1px solid rgba(34,197,94,0.25)',
              marginBottom: '1.25rem',
            }}>
              <div style={{ fontSize: '0.78rem', opacity: 0.75, marginBottom: '0.15rem' }}>Available balance</div>
              <div style={{ fontSize: '1.35rem', fontWeight: 700, color: '#22C55E' }}>
                ₦{Number(balance || 0).toLocaleString()}
              </div>
            </div>

            <div className="AutoboyExpress-dash-form-group" style={{ marginBottom: '1.1rem' }}>
              <label htmlFor="withdrawAmount" style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, color: labelColor }}>
                Withdrawal Amount (₦) *
              </label>
              <input
                id="withdrawAmount"
                type="number"
                min={MIN_WITHDRAWAL}
                step="100"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder={`Minimum ${MIN_WITHDRAWAL.toLocaleString()}`}
                style={inputStyle}
                required
              />
              <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.3rem' }}>
                Minimum withdrawal is ₦{MIN_WITHDRAWAL.toLocaleString()}. Funds arrive within 24 hours.
              </p>
            </div>

            <div className="AutoboyExpress-dash-form-group" style={{ marginBottom: '1.1rem' }}>
              <label htmlFor="withdrawBank" style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, color: labelColor }}>
                Destination Bank Account *
              </label>
              {loadingAccounts ? (
                <p style={{ fontSize: '0.9rem', opacity: 0.7 }}>
                  <FontAwesomeIcon icon={faSpinner} spin /> Loading your bank accounts…
                </p>
              ) : bankAccounts.length === 0 ? (
                <div style={{
                  padding: '1rem',
                  borderRadius: '10px',
                  background: isDarkMode ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.1)',
                  border: '1px solid rgba(245,158,11,0.3)',
                  fontSize: '0.875rem',
                }}>
                  <FontAwesomeIcon icon={faUniversity} style={{ color: '#F59E0B', marginRight: '0.4rem' }} />
                  You have no saved bank accounts. Add one from <strong>Bank Accounts</strong> before withdrawing.
                </div>
              ) : (
                <select
                  id="withdrawBank"
                  value={selectedBankId}
                  onChange={e => setSelectedBankId(e.target.value)}
                  style={inputStyle}
                  required
                >
                  <option value="">-- Select a saved bank account --</option>
                  {bankAccounts.map(account => (
                    <option key={account.id || account._id} value={account.id || account._id}>
                      {account.bank_name} — {account.account_number}
                      {account.is_verified ? ' ✓ Verified' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {selectedAccount && (
              <div style={{
                padding: '0.85rem 1rem',
                borderRadius: '10px',
                background: isDarkMode ? 'rgba(255,255,255,0.05)' : '#F9FAFB',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#E5E7EB'}`,
                fontSize: '0.85rem',
                marginBottom: '1rem',
              }}>
                <div><strong>{selectedAccount.account_name}</strong></div>
                <div style={{ opacity: 0.75 }}>{selectedAccount.bank_name} · {selectedAccount.account_number}</div>
              </div>
            )}

            {error && (
              <div style={{
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                color: '#DC2626',
                fontSize: '0.875rem',
                marginBottom: '1rem',
              }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.4rem' }} />
                {error}
              </div>
            )}
          </div>

          <div style={{
            padding: '1rem 2rem 1.5rem',
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'flex-end',
            borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : '#E5E7EB'}`,
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.7rem 1.4rem',
                borderRadius: '8px',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : '#D1D5DB'}`,
                background: 'transparent',
                color: isDarkMode ? '#E5E7EB' : '#374151',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || bankAccounts.length === 0}
              style={{
                padding: '0.7rem 1.6rem',
                borderRadius: '8px',
                border: 'none',
                background: submitting || bankAccounts.length === 0
                  ? '#9CA3AF'
                  : 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                color: 'white',
                fontWeight: 700,
                cursor: submitting || bankAccounts.length === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {submitting
                ? <><FontAwesomeIcon icon={faSpinner} spin /> Processing…</>
                : <><FontAwesomeIcon icon={faMoneyBillWave} /> Withdraw</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WalletWithdrawModal;
